import { HttpErrorResponse } from '@angular/common/http';
import { InjectionToken } from '@angular/core';
import { Auth } from 'src/app/auth/models/auth';
import { readStorage, removeStorage, writeStorage, writeStorageJson } from 'src/app/shared/utils/safe-storage';

/**
 * The identity a tab may fall back on when it boots with NO NETWORK: who was signed in on this
 * browser, with which roles, until when. It is written after every successful login / token refresh
 * and read only by the offline boot path (see `AuthRefreshService.bootstrap` and
 * `OfflineSessionService`).
 *
 * **What it is NOT: a credential.** No token, no secret, nothing the backend would accept. The
 * access token stays memory-only in `AccessTokenHolder` and the refresh cookie stays httpOnly; this
 * record cannot authenticate a single request. What it buys is narrow on purpose: a tab that cannot
 * reach the server at all may open the READ-ONLY offline catalogue (`/app/ingest/printings/**`,
 * `/app/ingest/sealed/**`) and Intelligence > Scan (`/app/ingest/scan/**`, the offline home) under
 * the name it last had. The trade-off is written down in CLAUDE.md ("Offline mode").
 *
 * **Where: `localStorage`, through safe-storage.** Not `sessionStorage`, because a tab opened while
 * offline (the case this exists for -- the laptop at a trade fair, browser restarted) has an empty
 * session area; not IndexedDB, because the boot decision is made inside `APP_INITIALIZER` and a
 * synchronous read keeps it out of a second async hop. Shared by every tab of the origin, which is
 * also what makes two offline tabs agree (and what lets a logout in one tab end the offline mode of
 * another, through the `storage` event).
 *
 * **validUntil = now + 8 h, slid on every refresh**: the refresh cookie's idle lifetime. Past it the
 * cookie is dead server-side anyway, so the identity is dropped rather than trusted.
 */

export const OFFLINE_IDENTITY_STORAGE_KEY = 'auth.offlineIdentity';

/** The refresh cookie's idle lifetime: the longest an unverifiable identity is worth anything. */
export const OFFLINE_IDENTITY_TTL_MS = 8 * 60 * 60 * 1000;

/**
 * Master switch of the offline SESSION (not of the offline catalogue, which works for any online
 * session regardless). Off ⇒ no identity is ever written and a network failure at boot routes to
 * /auth exactly as before. A token rather than an environment key so a spec can flip it.
 */
export const OFFLINE_SESSION_ENABLED = new InjectionToken<boolean>('OFFLINE_SESSION_ENABLED', {
  providedIn: 'root',
  factory: () => true,
});

export interface OfflineIdentity {
  /** Typed like `Auth.userId`; at runtime the token's string claim, kept as is (see `isUserId`). */
  userId: number;
  username: string;
  firstName: string;
  lastName: string;
  roles: string[];
  /** Epoch milliseconds. */
  validUntil: number;
}

/**
 * Record the identity a successful login / refresh just applied. An enrollment-scoped session is
 * NEVER recorded (and clears any record): a token still confined to the 2FA wizard must not be
 * re-admitted as anything, offline least of all.
 */
export function rememberOfflineIdentity(auth: Auth, enrollmentRequired: boolean, now = Date.now()): void {
  if (!auth || !auth.isAuthenticated || !auth.userId || enrollmentRequired) {
    forgetOfflineIdentity();
    return;
  }
  const record: OfflineIdentity = {
    userId: auth.userId,
    username: auth.username ?? '',
    firstName: auth.firstName ?? '',
    lastName: auth.lastName ?? '',
    roles: Array.isArray(auth.roles) ? [...auth.roles] : [],
    validUntil: now + OFFLINE_IDENTITY_TTL_MS,
  };
  // A refused write leaves the PREVIOUS record in place (safe-storage's rule), which here could be
  // another user's: drop it rather than let a stale identity outlive this login.
  if (!writeStorageJson('local', OFFLINE_IDENTITY_STORAGE_KEY, record)) {
    forgetOfflineIdentity();
  }
}

export function forgetOfflineIdentity(): void {
  removeStorage('local', OFFLINE_IDENTITY_STORAGE_KEY);
}

/**
 * A user id as a session really carries it: a positive integer, OR its canonical decimal string.
 * The backend writes the JWT claim as a STRING (`AuthenticationService`:
 * `claims.put("userId", Objects.toString(user.getId()))`) and `decodeAuthToken` passes it through
 * untyped, so a live session's `Auth.userId` is "109" whatever the `number` type says. Accepting only
 * numbers here made every real record "malformed": the cold offline boot deleted it and landed on
 * /auth (12/10/2026, installed PWA reopened in airplane mode). The value is kept AS IS, not coerced:
 * Scan's quotes owner and list keys were written with the session's value, and the offline tab must
 * compare equal to them.
 */
function isUserId(value: unknown): boolean {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value > 0;
  }
  return typeof value === 'string' && /^[1-9]\d{0,15}$/.test(value);
}

function isIdentity(value: unknown): value is OfflineIdentity {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    isUserId(v['userId']) &&
    typeof v['validUntil'] === 'number' &&
    Array.isArray(v['roles']) &&
    (v['roles'] as unknown[]).every((r) => typeof r === 'string')
  );
}

/**
 * The stored identity if it is well-formed and still valid; otherwise null -- and an expired or
 * malformed record is removed on the way, so nothing keeps re-reading it.
 */
export function readOfflineIdentity(now = Date.now()): OfflineIdentity | null {
  const raw = readStorage('local', OFFLINE_IDENTITY_STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    forgetOfflineIdentity();
    return null;
  }
  if (!isIdentity(parsed) || parsed.validUntil <= now) {
    forgetOfflineIdentity();
    return null;
  }
  return {
    userId: parsed.userId,
    username: typeof parsed.username === 'string' ? parsed.username : '',
    firstName: typeof parsed.firstName === 'string' ? parsed.firstName : '',
    lastName: typeof parsed.lastName === 'string' ? parsed.lastName : '',
    roles: [...parsed.roles],
    validUntil: parsed.validUntil,
  };
}

/** The display name an identity carries: first + last name, else the username. */
export function offlineIdentityName(identity: OfflineIdentity | null): string {
  if (!identity) {
    return '';
  }
  const full = `${identity.firstName} ${identity.lastName}`.trim();
  return full || identity.username;
}

/**
 * A request that never reached a server: `HttpErrorResponse` with status 0 (DNS failure, no route,
 * connection refused, aborted by the browser). Deliberately NOT a 5xx: a gateway answering 502 is a
 * network that works and a backend that does not, and the offline session is for the former only.
 */
export function isNetworkError(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 0;
}

/**
 * The SERVER refused the session: 401 or 403 on the refresh, the cookie is dead or revoked. The only
 * answer that makes the remembered identity worthless. Anything else -- a 5xx during a deploy, a
 * captive portal answering 200 with an HTML page (a parse error), a status 0 -- says nothing about
 * the cookie, so the identity is kept (review 25/10/2026: those used to forget it and send an
 * offline tab to /auth).
 */
export function isAuthRejection(error: unknown): boolean {
  return error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403);
}

/**
 * The browser itself says it has no network (`navigator.onLine === false`): THE condition of the
 * offline boot. Once {@link isAuthRejection} has ruled out the server refusing the cookie (that
 * forgets the identity), any failure of the boot refresh -- a status 0, a 5xx, a parse error --
 * enters offline mode only when this is true. A failure with `onLine` true is a server that could
 * not be reached FROM a working network (a gateway restarting, a deploy, a DNS hiccup), and product
 * owner 25/10/2026 wants offline mode "solo se davvero senza rete": such a boot keeps the online
 * behaviour (login with the return URL) instead of locking an online user into the catalogue.
 */
export function browserReportsOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * A logout the server never heard of. `AuthService.logout()` revokes this device's refresh family
 * with POST /v2/logout, but an offline tab cannot send it: the interceptor refuses every request
 * but /v2/refresh in offline mode, and with no network it fails with status 0. The local teardown
 * runs anyway, so the refresh COOKIE stays alive -- and the next online boot's refresh would
 * silently sign the user who logged out back in (review round 2, 25/10/2026).
 *
 * So the logout is recorded here and completed by the next boot (`AuthRefreshService.bootstrap`),
 * which posts /v2/logout INSTEAD of the refresh. `localStorage`, like the offline identity: the
 * flag must survive the browser being closed, and every tab of the origin shares the one cookie it
 * is about. Cleared once the server answered the logout, or by a new login (whose cookie replaces
 * the one the flag was about).
 */
export const LOGOUT_PENDING_STORAGE_KEY = 'auth.logoutPending';

export function markLogoutPending(now = Date.now()): void {
  writeStorage('local', LOGOUT_PENDING_STORAGE_KEY, String(now));
}

export function isLogoutPending(): boolean {
  return readStorage('local', LOGOUT_PENDING_STORAGE_KEY) !== null;
}

export function clearLogoutPending(): void {
  removeStorage('local', LOGOUT_PENDING_STORAGE_KEY);
}
